import { AnimatePresence } from 'framer-motion'
import { Columns } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/utils/cn'
import { formatCurrencyCompact } from '@/utils/format-currency'
import { getStageLabel, getStageColor } from '@/components/Deals/deals-constants'
import { RocketKanbanCard } from '@/components/RocketSearch/RocketKanbanCard'
import type { Deal, DealStage } from '@/types'

interface RocketKanbanColumnProps {
  stage: DealStage
  stageDeals: Deal[]
  draggedDealId: string | null
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent, stage: DealStage) => void
  onDragStart: (e: React.DragEvent<HTMLDivElement>, dealId: string) => void
  onDragEnd: (e: React.DragEvent<HTMLDivElement>) => void
  onDealClick?: (deal: Deal) => void
  onDelete?: (dealId: string) => void
}

export function RocketKanbanColumn({
  stage,
  stageDeals,
  draggedDealId,
  onDragOver,
  onDrop,
  onDragStart,
  onDragEnd,
  onDealClick,
  onDelete,
}: RocketKanbanColumnProps) {
  const stageTotal = stageDeals.reduce((acc, d) => acc + (d.valor || 0), 0)

  return (
    <div
      className='w-72 shrink-0'
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, stage)}
    >
      <div className='mb-2 flex items-center justify-between px-1'>
        <div className='flex min-w-0 items-center gap-2'>
          <div
            className='h-2.5 w-2.5 shrink-0 rounded-full'
            style={{ backgroundColor: getStageColor(stage) }}
          />
          <span className='truncate text-xs font-semibold text-[var(--font-color-body)]'>
            {getStageLabel(stage)}
          </span>
          <Badge variant='muted' className='text-[10px]'>
            {stageDeals.length}
          </Badge>
        </div>
      </div>

      <div
        className={cn(
          'min-h-[120px] space-y-2 rounded-lg border border-white/[0.06] bg-white/[0.02] p-2 transition-colors',
          draggedDealId && 'border-dashed border-[var(--brand-primary)]/40',
        )}
      >
        {stageDeals.length === 0 && (
          <div className='flex flex-col items-center justify-center py-8 text-[var(--font-color-muted)]'>
            <Columns size={22} className='mb-2 opacity-30' />
            <span className='text-[11px] italic'>Nenhum deal</span>
            <span className='mt-0.5 text-[9px] opacity-50'>Arraste deals para cá</span>
          </div>
        )}

        <AnimatePresence>
          {stageDeals.map((deal) => (
            <RocketKanbanCard
              key={deal.id}
              deal={deal}
              stage={stage}
              draggedDealId={draggedDealId}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDealClick={onDealClick}
              onDelete={onDelete}
            />
          ))}
        </AnimatePresence>

        {stageDeals.length > 0 && (
          <div className='mt-1 border-t border-white/[0.06] pt-1'>
            <p className='text-center text-[10px] text-[var(--font-color-muted)]'>
              Total: {formatCurrencyCompact(stageTotal)}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
